import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader, SCUICustomDataGrid } from '../../../atomic/molecules';
import type { AutoReturnReport, AutoReturnReportDetailItem } from '../autoReturnAndResend.types';
import AutoReturnAndResendQuery from '../../../services/query/autoReturnAndResend/AutoReturnAndResendQuery.services';
import {
    getReportDate,
    getReportId,
    getReportItems,
    getReportUserName,
    isSuccessfulReportItem,
    normalizeAutoReturnReportDetails,
} from '../autoReturnAndResend.helpers';
import { dataGridSx, renderLinkCell } from '../autoReturnAndResend.gridHelpers';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendReportDetailDrawerProps {
    open: boolean;
    report: AutoReturnReport | null;
    onClose: () => void;
}

export const AutoReturnAndResendReportDetailDrawer: FC<AutoReturnAndResendReportDetailDrawerProps> = ({
    open,
    report,
    onClose,
}) => {
    const { t } = useTranslation();
    const [detailReport, setDetailReport] = useState<AutoReturnReport | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !report) {
            setDetailReport(null);
            return;
        }

        const rpaRequestId = report.rpaRequestId ?? report.reportId;
        if (rpaRequestId == null) {
            return;
        }

        let isMounted = true;
        setLoading(true);

        AutoReturnAndResendQuery.GetAutoReturnReportDetails({ rpaRequestId })
            .then((response) => {
                if (isMounted) {
                    setDetailReport(normalizeAutoReturnReportDetails(response, report));
                }
            })
            .catch((error) => {
                console.error('Error during GetAutoReturnReportDetails:', error);
                if (isMounted) {
                    setDetailReport(report);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [open, report]);

    const displayedReport = detailReport ?? report;

    const excelDownloadData = useMemo(() => {
        if (!displayedReport) {
            return [];
        }

        return getReportItems(displayedReport).map((item) => ({
            [t('general.orderNumber')]: item.orderNumber ?? '-',
            [t('general.shippingNo')]: item.shippingListNo ?? '-',
            [t('autoReturnAndResend.columns.status')]:
                isSuccessfulReportItem(item)
                    ? t('autoReturnAndResend.reports.itemStatus.success')
                    : t('autoReturnAndResend.reports.itemStatus.failed'),
        }));
    }, [displayedReport, t]);

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'orderNumber',
                headerName: t('general.orderNumber'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?OrderNumber=${params.row.orderNumber}`, params.row.orderNumber),
            },
            {
                field: 'shippingListNo',
                headerName: t('general.shippingNo'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(
                        `/orderdetail?shippingListNo=${params.row.shippingListNo}`,
                        params.row.shippingListNo
                    ),
            },
            {
                field: 'status',
                headerName: t('autoReturnAndResend.columns.status'),
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params: { row: AutoReturnReportDetailItem }) => (
                    <Chip
                        size="small"
                        color={isSuccessfulReportItem(params.row) ? 'success' : 'error'}
                        label={
                            isSuccessfulReportItem(params.row)
                                ? t('autoReturnAndResend.reports.itemStatus.success')
                                : t('autoReturnAndResend.reports.itemStatus.failed')
                        }
                    />
                ),
            },
        ],
        [t]
    );

    if (!displayedReport) {
        return null;
    }

    return (
        <SCUIGeneralDrawer
            open={open}
            anchor="right"
            width="600px"
            onClose={(_event: unknown, reason: string) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    onClose();
                }
            }}
        >
            <SCUIGeneralDrawerHeader header={t('autoReturnAndResend.reports.drawerTitle')} onClose={onClose} />

            <Box className={ARAR.reportDrawer} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {getReportId(displayedReport)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {getReportDate(displayedReport)} · {getReportUserName(displayedReport)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        color="info"
                        size="small"
                        label={`${t('autoReturnAndResend.reports.summary.total')}: ${
                            displayedReport.totalCount ?? getReportItems(displayedReport).length
                        }`}
                    />
                    <Chip
                        color="success"
                        size="small"
                        label={`${t('autoReturnAndResend.reports.summary.success')}: ${
                            displayedReport.successCount ??
                            getReportItems(displayedReport).filter(isSuccessfulReportItem).length
                        }`}
                    />
                    <Chip
                        color="error"
                        size="small"
                        label={`${t('autoReturnAndResend.reports.summary.failed')}: ${
                            displayedReport.failedCount ??
                            getReportItems(displayedReport).filter((item) => !isSuccessfulReportItem(item)).length
                        }`}
                    />
                </Box>

                <Box className={ARAR.panelGrid} sx={dataGridSx}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <SCUICustomDataGrid
                            getRowId={(row: AutoReturnReportDetailItem) =>
                                `${row.shippingListNo}-${row.orderNumber ?? ''}`
                            }
                            rows={getReportItems(displayedReport)}
                            columns={columns}
                            pageSize={10}
                            autoHeight
                            excelDownloadButton
                            excelDownloadData={excelDownloadData}
                            excelDownloadButtonName={t('rpaResender.exportToExcel')}
                            localeText={t('autoReturnAndResend.reports.noReportItems')}
                        />
                    )}
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};
